/**
 * Shown after sign-in when an account holds more than one role that this app can do something
 * with: choose which one you mean before the app opens.
 *
 * The choice is a control rather than a preference — the API narrows the caller's claims to the
 * role chosen — so the wording says what each one *can do*, not what it is called. Somebody
 * picking between "Platform administrator" and "Room operator" is choosing between every room and
 * their own, and the label alone does not say that.
 *
 * Written against the shared tokens (`bg-ground`, `bg-surface`, `border-line`), so it inverts to
 * the on-air palette by rendering inside `.onair` — which is how player-web gets the charcoal
 * ground without a second component. The accent appears only on the control under the pointer:
 * choosing is an action, and in the gallery orange means "you must act", but an orange block per
 * option would spend the signal on all of them at once.
 */
export interface RoleChoice {
  /** Opaque to this component — whatever the app calls the role. */
  id: string;
  label: string;
  /** What this role can do here, in the app's own terms. One sentence. */
  description: string;
}

export function RoleChooser({
  appName,
  choices,
  onChoose,
  accountName,
  onSignOut,
  notice,
  currentId,
  onCancel,
}: {
  appName: string;
  choices: readonly RoleChoice[];
  onChoose: (id: string) => void;
  accountName?: string | null;
  onSignOut?: (() => void) | null;
  /**
   * Why the chooser is being shown again, when it is not the first time. The case that matters is
   * a stored choice the server has stopped accepting — an assignment withdrawn between sessions —
   * which without a sentence here reads as the app having forgotten who you are.
   */
  notice?: string | null;
  /** The role in force, when this is a switch rather than a first choice. */
  currentId?: string | null;
  /** Offered only when there is something to go back to — i.e. when switching. */
  onCancel?: (() => void) | null;
}) {
  return (
    <div className="grid min-h-screen place-items-center bg-ground p-6">
      <div
        className="w-full max-w-md border border-line bg-surface p-8"
        style={{ borderTopWidth: 'var(--panel-rule)', borderTopColor: 'var(--color-signal)' }}
      >
        <h1 className="font-display text-lg font-semibold text-ink-950">
          {currentId ? 'Switch role' : `Choose your role for ${appName}`}
        </h1>
        <p className="mt-2 text-sm text-ink-800">
          Your account holds more than one. What you can see and change depends on which you pick,
          and you can switch at any time.
        </p>

        {notice ? (
          <p
            className="mt-4 border px-3 py-2 text-xs text-ink-800"
            style={{ borderColor: 'var(--color-bad)' }}
          >
            {notice}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col gap-2">
          {choices.map((choice) => {
            const isCurrent = currentId === choice.id;
            return (
              <button
                key={choice.id}
                type="button"
                onClick={() => onChoose(choice.id)}
                aria-current={isCurrent ? 'true' : undefined}
                className="group border border-line px-4 py-3 text-left transition hover:border-signal focus:border-signal focus:outline-none"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-ink-950">
                    {choice.label}
                  </span>
                  {isCurrent ? (
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-800">
                      Current
                    </span>
                  ) : null}
                </span>
                <span className="mt-1 block text-xs text-ink-800">{choice.description}</span>
              </button>
            );
          })}
        </div>

        {accountName ? (
          <p className="mt-6 border border-line bg-felt-50 px-3 py-2 text-xs text-ink-800">
            Signed in as <span className="font-semibold text-ink-950">{accountName}</span>
          </p>
        ) : null}

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="mt-4 w-full border border-line py-2 text-xs font-bold uppercase tracking-[0.08em] text-ink-800 transition hover:bg-felt-50"
          >
            Keep my current role
          </button>
        ) : null}

        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            className="mt-2 w-full border border-line py-2 text-xs font-bold uppercase tracking-[0.08em] text-ink-800 transition hover:bg-felt-50"
          >
            Sign out and use a different account
          </button>
        ) : null}
      </div>
    </div>
  );
}
